// Surveys.jsx
import React, { useEffect, useState } from "react";
import {
  Button,
  Snackbar,
  Alert,
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  DialogActions,
} from "@mui/material";
import axios from "axios";
import CardIconAndTitle from "../../components/CardIconAndTitle";
import Navbar from "../../components/navbar/Navbar";
import Footer from "../../components/navbar/Footer";

const ISPDashboard = () => {
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);

  const fetchISPDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/getISPDashboard`
      );
      setDashboardData(response.data);
    } catch (e) {
      setToast({
        open: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchISPDashboard();
  }, []);

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return;
    setToast((t) => ({ ...t, open: false }));
  };

  const getStatusChip = (response) => {
    const lastArea = response.lastarea;
    console.log("a", lastArea);
    const isFullyDone = Number(lastArea) >= 15;
    return (
      <Chip
        label={
          isFullyDone && response.pass
            ? "Passed"
            : isFullyDone && !response.pass
            ? "Failed"
            : "In Progress"
        }
        color={
          isFullyDone && response.pass
            ? "success"
            : isFullyDone && !response.pass
            ? "error"
            : "warning"
        }
        size="small"
        variant="filled"
      />
    );
  };

  const getPassCount = () => {
    const passItems = dashboardData?.surveys?.filter(
      (item) => Number(item.pass) === 1
    );
    return passItems?.length;
  };
  const getFailCount = () => {
    const failItems = dashboardData?.surveys?.filter(
      (item) => Number(item.pass) === 0 && Number(item.lastarea) >= 15
    );
    return failItems?.length;
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const isSilverInterested = (email) => {
    return dashboardData?.silverRegistrations?.some(
      (registration) => registration.email === email
    );
  };
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "200px",
          gap: 2,
        }}
      >
        <CircularProgress
          size={40}
          thickness={4}
          sx={{
            color: "#091f33",
          }}
        />
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            fontWeight: 500,
          }}
        >
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <div className="flex h-[100vh] overflow-auto">
        <div className="flex flex-col flex-1">
          <header className="bg-white shadow-md">
            <Navbar />
          </header>
          <Container maxWidth="xl">
            <Box display="flex" justifyContent="space-between" mb={2} mt={2}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                ISP Bronze Dashboard
              </Typography>{" "}
            </Box>
            {/* Summary Cards */}
            <Grid container spacing={2} mb={3}>
              {/* First Row - Main Metrics */}
              <Grid item xs={12} sm={6} md={3}>
                <CardIconAndTitle
                  title="Responses"
                  score={dashboardData?.responses || 0}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CardIconAndTitle
                  title="Completion Status"
                  dualValues={{
                    value1: dashboardData?.fullyDone || 0,
                    label1: "Completed",
                    value2: dashboardData?.partlyDone || 0,
                    label2: "In Progress",
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CardIconAndTitle
                  title="Pass/Fail Status"
                  dualValues={{
                    value1: getPassCount() || 0,
                    label1: "Pass",
                    value2: getFailCount() || 0,
                    label2: "Fail",
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  onClick={handleOpenDialog}
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardIconAndTitle
                    title="Interested in Silver"
                    score={dashboardData?.silverRegistrations?.length || 0}
                    subtitle="Click to view list"
                  />
                </Box>
              </Grid>

              {/* Second Row - Score Cards */}
              <Grid item xs={12} sm={6} md={3}>
                <CardIconAndTitle
                  title="ISP Score"
                  score={dashboardData?.ISPScore || 0}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CardIconAndTitle
                  title="Individual Score"
                  score={dashboardData?.individualScore || 0}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CardIconAndTitle
                  title="Support Score"
                  score={dashboardData?.supportScore || 0}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CardIconAndTitle
                  title="Process Score"
                  score={dashboardData?.processScore || 0}
                />
              </Grid>
            </Grid>

            {/* Survey Responses Table */}
            {/* {surveyDetails?.results && surveyDetails.results.length > 0 && ( */}
            <Box mb={3}>
              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                  borderRadius: 2,
                  overflowX: "auto",
                  maxWidth: "100vw",
                }}
              >
                <Table sx={{ minWidth: 650 }} aria-label="ISP Surveys">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#D3D3D3" }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                        #
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                        Email
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                        Name
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                        align="center"
                      >
                        Survey Done On
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "0.9rem", pr: 1 }}
                        align="center"
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "0.9rem", pl: 1 }}
                        align="center"
                      >
                        Reminder Sent
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                        align="center"
                      >
                        Score
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                        align="center"
                      >
                        Individual
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                        align="center"
                      >
                        Support
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                        align="center"
                      >
                        Process
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "0.9rem" }}
                        align="center"
                      >
                        Silver?
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData?.surveys?.map((response, index) => (
                      <TableRow
                        key={response.id}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "#fafafa",
                          },
                          "&:hover": {
                            backgroundColor: "#f0f8ff",
                          },
                          transition: "background-color 0.2s ease",
                        }}
                      >
                        <TableCell sx={{ fontSize: "0.85rem" }}>
                          {index + 1}
                        </TableCell>

                        <TableCell sx={{ fontSize: "0.85rem", maxWidth: 200 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {response.email}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {response.company_name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                          <Typography variant="body2">
                            {formatDate(response.updated_on)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ pr: 1 }} align="center">
                          {getStatusChip(response)}
                        </TableCell>
                        {/* <TableCell align="center" sx={{ pl: 1 }}>
                          {response.pass ? (
                            <Chip
                              label="Pass"
                              color="success"
                              size="small"
                              variant="outlined"
                            />
                          ) : !response.pass && response?.lastarea >= 15 ? (
                            <Chip
                              label="Fail"
                              color="error"
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <>-</>
                          )}
                        </TableCell> */}
                        <TableCell align="center" sx={{ pl: 1 }}>
                          {response.reminderSentOn?.split(" ")[0]}
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {response?.score}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {response?.individualScore}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {response?.supportScore}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ fontSize: "0.85rem" }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {response?.processScore}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {response.lastarea >= 15 ? (
                            isSilverInterested(response.email) ? (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  backgroundColor: "#4caf50",
                                  color: "white",
                                }}
                              >
                                <Typography
                                  sx={{ fontSize: "1.1rem", fontWeight: 600 }}
                                >
                                  ✓
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* )} */}

            <Snackbar
              open={toast.open}
              autoHideDuration={5000}
              onClose={handleClose}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <Alert
                onClose={handleClose}
                severity={toast.severity}
                variant="filled"
              >
                {toast.message}
              </Alert>
            </Snackbar>
          </Container>
          <Footer />
        </div>
      </div>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Interested In Silver</DialogTitle>
        <DialogContent dividers>
          {dashboardData?.silverRegistrations?.length > 0 ? (
            <List>
              {dashboardData.silverRegistrations.map((registration, index) => (
                <ListItem
                  key={index}
                  divider={index < dashboardData.silverRegistrations.length - 1}
                >
                  <ListItemText
                    primary={registration.email}
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              No silver registrations available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ISPDashboard;
